import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout, Square, Rows, Columns, ChevronDown, Radio, Loader2, RotateCcw, AlertTriangle, HelpCircle, X } from 'lucide-react';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import CameraPreview from '../components/game/CameraPreview';
import { streamBroadcastService } from '../services/streaming/StreamBroadcastService';
import { StreamingPlatform, BroadcastEvent } from '../types/streaming';
import { useGameBridge } from '../hooks/useGameBridge';
import { SDKUserData, AppPreferences } from '../services/game/GameBridgeService';
import { useThemeStore } from '../store/useThemeStore';
import { useGamesStore } from '../store/useGamesStore';
import authService from '../services/authService';
import { LINE_GRADIENT } from '../constants/gradients';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { webplaySessionService } from '../services/webplaySessionService';
import { webplayGameLogService } from '../services/webplayGameLogService';
import { webplayStreamService } from '../services/webplayStreamService';
import { useStreamingLimits } from '../hooks/useStreamingLimits';
import StreamingLimitsOverlay from '../components/streaming/StreamingLimitsOverlay';
import { useAnalytics } from '../hooks/useAnalytics';

type LayoutMode = 'game-only' | 'portrait' | 'landscape';

interface StreamConnectionInfo {
  isConnected: boolean;
  platform: StreamingPlatform | null;
  username: string | null;
  viewerCount: number;
  likeCount: number;
  commentCount: number;
  title: string | null;
  streamImageUrl: string | null;
  profileImageUrl: string | null;
}

export default function PlayPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Preview mode — activated when the studio passes a ?psu= (preview server URL) param
  const previewServerUrl = searchParams.get('psu') || '';
  const previewMode = !!previewServerUrl;
  const { trackGameAction, trackStreamAction } = useAnalytics();
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('game-only');
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [gameScale, setGameScale] = useState(1);
  const [manualLayoutChange, setManualLayoutChange] = useState(false);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [isOverlayFading, setIsOverlayFading] = useState(false);
  const [isOverlayGone, setIsOverlayGone] = useState(false);
  const parentContainerRef = useRef<HTMLDivElement>(null);
  const gameIframeRef = useRef<HTMLIFrameElement>(null);
  const streamConnectorRef = useRef<Window | null>(null);
  
  // Streaming limits integration
  const streamingLimits = useStreamingLimits();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // User and preferences state
  const [user, setUser] = useState<SDKUserData | null>(null);
  const { isDark } = useThemeStore();
  
  // Stream connection info from BroadcastChannel
  const [streamInfo, setStreamInfo] = useState<StreamConnectionInfo>({
    isConnected: false,
    platform: null,
    username: null,
    viewerCount: 0,
    likeCount: 0,
    commentCount: 0,
    title: null,
    streamImageUrl: null,
    profileImageUrl: null
  });
  
  // Ref to always have access to latest streamInfo (for async callbacks)
  const streamInfoRef = useRef(streamInfo);
  useEffect(() => {
    streamInfoRef.current = streamInfo;
  }, [streamInfo]);
  
  // Fetch game from store
  const { 
    currentGame: game, 
    isLoadingGame, 
    gameError, 
    fetchGameById 
  } = useGamesStore();
  
  // Session and game tracking
  const { isVisible, visibilityChanges } = usePageVisibility();
  const [pageLoadStartTime] = useState(Date.now());
  const [gameLogId, setGameLogId] = useState<string | null>(null);
  const [streamLogId, setStreamLogId] = useState<string | null>(null);
  const isStartingStreamRef = useRef(false); // Guard against double startStream calls while async is in-flight
  const iframeLoadStartTime = useRef<number>(0);
  
  // Check if opened in popup window
  const isPopup = window.opener !== null;
  
  // Set popup window title
  useEffect(() => {
    if (previewMode) {
      document.title = 'Beemi Studio Preview';
    } else if (isPopup && !isLoadingGame && game) {
      document.title = 'Beemi Game Capture';
    }
  }, [previewMode, isPopup, isLoadingGame, game]);
  
  // Create app preferences from theme store
  const appPreferences = useMemo<AppPreferences>(() => ({
    language: navigator.language.split('-')[0] || 'en',
    region: navigator.language.split('-')[1] || 'US',
    theme: isDark ? 'dark' : 'light',
    locale: navigator.language || 'en-US'
  }), [isDark]);
  
  // Handle auth prompt from game (redirect to login)
  const handleAuthPrompt = useCallback(() => {
    // Open login in a new window if in popup, otherwise navigate
    if (isPopup) {
      window.open('/login', '_blank');
    } else {
      navigate('/login');
    }
  }, [isPopup, navigate]);
  
  // Initialize game bridge hook
  const { 
    attachToIframe, 
    detach, 
    isSDKReady, 
    sdkVersion,
    isAttached,
    sendTestMessage
  } = useGameBridge({
    user,
    preferences: appPreferences,
    onAuthPrompt: handleAuthPrompt,
    debug: true // Enable debug logging
  });
  
  // Expose test function on window for easy debugging
  useEffect(() => {
    (window as any).testGameMessage = sendTestMessage;
    console.log('🧪 Test function available: window.testGameMessage()');
  }, [sendTestMessage]);
  
  // Fetch game and user data on mount (skipped in preview mode — no catalog entry needed)
  useEffect(() => {
    if (gameId && !previewMode) {
      fetchGameById(gameId);
    }
  }, [gameId, fetchGameById, previewMode]);
  
  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (authService.isAuthenticated()) {
          const userData = await authService.getCurrentUser();
          const userObj = userData.user || userData;
          if (userObj && userObj.id) {
            setUser({
              id: userObj.id,
              username: userObj.username || '',
              display_name: userObj.display_name || userObj.username || '',
              image_url: userObj.image_url || null
            });
          }
        }
      } catch (error) {
        console.log('[PlayPage] User not authenticated or failed to fetch:', error);
      }
    };
    
    fetchUser();
  }, []);
  
  // Start game tracking when game loads
  useEffect(() => {
    const sessionId = webplaySessionService.getCurrentSessionId();
    if (!game || !sessionId || isLoadingGame || gameLogId) {
      return;
    }

    const startGameTracking = async () => {
      try {
        const pageLoadTimeMs = Date.now() - pageLoadStartTime;
        
        // Check if this is first play after purchase
        const purchaseTimestampStr = localStorage.getItem(`beemi_purchase_${game.id}`);
        if (purchaseTimestampStr) {
          const purchaseTimestamp = parseInt(purchaseTimestampStr, 10);
          const timeSincePurchase = Date.now() - purchaseTimestamp;
          const THIRTY_MINUTES = 30 * 60 * 1000;
          
          // If purchased within last 30 minutes, track first play
          if (timeSincePurchase < THIRTY_MINUTES) {
            trackGameAction('purchased_game_first_play', game.id.toString(), {
              game_name: game.name,
              time_since_purchase_ms: timeSincePurchase,
              played_immediately: timeSincePurchase < 5 * 60 * 1000 // Within 5 minutes
            });
            
            // Remove the purchase timestamp so we don't track this again
            localStorage.removeItem(`beemi_purchase_${game.id}`);
          }
        }
        
        // Track game started in Amplitude
        trackGameAction('game_started', game.id.toString(), {
          game_name: game.name,
          page_load_time_ms: pageLoadTimeMs,
          is_popup: isPopup
        });
        
        const logId = await webplayGameLogService.startGame({
          gameId: game.id,
          sessionId,
          pageLoadTimeMs,
          bundleVersion: game.bundle_version,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          gameLayout: layoutMode
        });
        
        setGameLogId(logId);
        console.log('✅ [PlayPage] Game tracking started:', logId);
      } catch (error) {
        console.error('❌ [PlayPage] Failed to start game tracking:', error);
      }
    };

    startGameTracking();
  }, [game, isLoadingGame, gameLogId, pageLoadStartTime, layoutMode]);

  // Track page visibility changes
  useEffect(() => {
    webplaySessionService.trackVisibilityChange(isVisible);
    
    if (webplayGameLogService.hasActiveSession()) {
      webplayGameLogService.trackVisibilityChange(isVisible);
    }
  }, [isVisible]);

  // Periodic metrics update (every 30 seconds)
  useEffect(() => {
    if (!gameLogId) return;

    const interval = setInterval(() => {
      webplayGameLogService.updateMetrics({
        totalPlayTimeMs: webplayGameLogService.getTotalPlayTime(),
        totalPauseTimeMs: webplayGameLogService.getTotalPauseTime(),
        visibilityChanges: visibilityChanges
      });
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [gameLogId, visibilityChanges]);

  // End game tracking on unmount, game change, or page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (webplayGameLogService.hasActiveSession()) {
        // Use beacon for guaranteed delivery during page unload
        webplayGameLogService.endGame(true);
      }
      if (webplayStreamService.hasActiveStream()) {
        webplayStreamService.endStream();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (webplayGameLogService.hasActiveSession()) {
        // Read via ref so this cleanup doesn't re-run every time stream connects/disconnects.
        // Having streamInfo.isConnected in deps was causing endGame() to fire on stream connect.
        if (game) {
          trackGameAction('game_ended', game.id.toString(), {
            game_name: game.name,
            was_stream_connected: streamInfoRef.current.isConnected
          });
        }
        webplayGameLogService.endGame(false);
      }
      if (webplayStreamService.hasActiveStream()) {
        webplayStreamService.endStream();
      }
    };
  }, [game, trackGameAction]);

  // Track stream connection/disconnection
  useEffect(() => {
    const sessionId = webplaySessionService.getCurrentSessionId();
    
    if (streamInfo.isConnected && streamInfo.platform && game && sessionId && !streamLogId && !isStartingStreamRef.current) {
      // Stream just connected - start tracking
      // Use a small delay to allow room-info event to be processed first
      const startStreamTracking = async () => {
        // Lock to prevent duplicate calls while this async operation is in-flight
        isStartingStreamRef.current = true;

        // Wait for React to process any pending room-info state updates
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Get latest streamInfo from ref (after delay)
        const latestStreamInfo = streamInfoRef.current;
        
        try {
          console.log('📡 [PlayPage] Stream connected, starting tracking:', {
            platform: latestStreamInfo.platform,
            username: latestStreamInfo.username,
            title: latestStreamInfo.title,
            streamImageUrl: latestStreamInfo.streamImageUrl,
            profileImageUrl: latestStreamInfo.profileImageUrl
          });

          const shareToDiscord = localStorage.getItem('beemi-share-to-discord') !== 'false';

          const logId = await webplayStreamService.startStream({
            sessionId,
            gameId: game.id,
            streamingPlatform: latestStreamInfo.platform!,
            channelId: latestStreamInfo.username || 'unknown',
            streamerName: latestStreamInfo.username || 'unknown',
            streamUrl: latestStreamInfo.platform === 'youtube'
              ? `https://youtube.com/@${latestStreamInfo.username}/live`
              : latestStreamInfo.platform === 'twitch'
              ? `https://twitch.tv/${latestStreamInfo.username}`
              : `https://tiktok.com/@${latestStreamInfo.username}/live`,
            streamInfo: {
              title: latestStreamInfo.title,
              streamImageUrl: latestStreamInfo.streamImageUrl,
              profileImageUrl: latestStreamInfo.profileImageUrl,
              initialViewerCount: latestStreamInfo.viewerCount,
              initialLikeCount: latestStreamInfo.likeCount,
              initialCommentCount: latestStreamInfo.commentCount
            },
            shareToDiscord
          });

          if (logId) {
            setStreamLogId(logId);
            console.log('✅ [PlayPage] Stream tracking started:', logId);
            
            // Track stream connected in Amplitude
            trackStreamAction('stream_connected', latestStreamInfo.platform!, {
              username: latestStreamInfo.username,
              game_id: game.id.toString(),
              game_name: game.name
            });
          }
        } catch (error) {
          console.error('❌ [PlayPage] Failed to start stream tracking:', error);
        } finally {
          isStartingStreamRef.current = false;
        }
      };

      startStreamTracking();
    } else if (!streamInfo.isConnected && streamLogId) {
      // Stream disconnected - end tracking
      const endStreamTracking = async () => {
        try {
          console.log('🏁 [PlayPage] Stream disconnected, ending tracking');
          await webplayStreamService.endStream();
          setStreamLogId(null);
        } catch (error) {
          console.error('❌ [PlayPage] Failed to end stream tracking:', error);
        }
      };

      endStreamTracking();
    }
  }, [streamInfo.isConnected, streamInfo.platform, streamInfo.username, game, streamLogId]);

  // Update stream metrics periodically
  useEffect(() => {
    if (!streamLogId || !streamInfo.isConnected) {
      return;
    }

    const interval = setInterval(() => {
      webplayStreamService.updateMetrics({
        viewerCount: streamInfo.viewerCount,
        likeCount: streamInfo.likeCount,
        commentCount: streamInfo.commentCount
      });
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [streamLogId, streamInfo.isConnected, streamInfo.viewerCount, streamInfo.likeCount]);
  
  // Handle iframe load - attach bridge and track load time
  useEffect(() => {
    if (isIframeReady && !isLoadingGame && !isOverlayFading) {
      setIsOverlayFading(true);
      const t = setTimeout(() => setIsOverlayGone(true), 600);
      return () => clearTimeout(t);
    }
  }, [isIframeReady, isLoadingGame]);

  const handleIframeLoad = useCallback(() => {
    setIsIframeReady(true);
    if (gameIframeRef.current) {
      attachToIframe(gameIframeRef.current);
      
      // Track iframe load time if we have a start time
      if (iframeLoadStartTime.current > 0) {
        const iframeLoadTimeMs = Date.now() - iframeLoadStartTime.current;
        console.log('⏱️ [PlayPage] Iframe loaded in', iframeLoadTimeMs, 'ms');
        
        // Update game log with iframe load time if active
        if (webplayGameLogService.hasActiveSession()) {
          webplayGameLogService.updateMetrics({
            customMetrics: { iframeLoadTimeMs }
          });
        }
      }
    }
  }, [attachToIframe]);
  
  // Cleanup bridge on unmount only (not when isAttached changes)
  useEffect(() => {
    return () => {
      detach();
    };
  }, [detach]);

  // Subscribe to BroadcastChannel for stream info updates (UI only)
  useEffect(() => {
    const unsubscribe = streamBroadcastService.subscribe((event: BroadcastEvent) => {
      // Only handle connection state and metrics for UI display
      // Stream events are forwarded to game via useGameBridge hook
      switch (event.type) {
        case 'connection-state':
          setStreamInfo(prev => ({
            ...prev,
            isConnected: event.payload.state === 'connected',
            platform: event.payload.platform,
            username: event.payload.username,
            title: event.payload.title || prev.title,
            streamImageUrl: event.payload.streamImageUrl || prev.streamImageUrl,
            profileImageUrl: event.payload.profileImageUrl || prev.profileImageUrl
          }));
          break;
          
        case 'stream-metrics':
          setStreamInfo(prev => ({
            ...prev,
            viewerCount: event.payload.viewerCount || 0,
            likeCount: event.payload.likeCount || 0,
            commentCount: event.payload.commentCount || 0
          }));
          break;

        case 'room-info':
          // Handle room/stream information
          setStreamInfo(prev => ({
            ...prev,
            title: event.payload.title || prev.title,
            streamImageUrl: event.payload.streamImageUrl || event.payload.rawData?.cover || event.payload.rawData?.thumbnail || prev.streamImageUrl,
            profileImageUrl: event.payload.streamerInfo?.profileImageUrl || prev.profileImageUrl
          }));
          break;

        case 'state-response':
          // Handle state response from stream connector
          if (event.payload.connectionState === 'connected') {
            setStreamInfo(prev => ({
              ...prev,
              isConnected: true,
              platform: event.payload.platform,
              username: event.payload.username,
              viewerCount: event.payload.metrics?.viewerCount || prev.viewerCount,
              likeCount: event.payload.metrics?.likeCount || prev.likeCount,
              commentCount: event.payload.metrics?.commentCount || prev.commentCount,
              title: event.payload.roomInfo?.title || event.payload.title || prev.title,
              streamImageUrl: event.payload.roomInfo?.streamImageUrl || event.payload.roomInfo?.rawData?.cover || event.payload.roomInfo?.rawData?.thumbnail || event.payload.streamImageUrl || prev.streamImageUrl,
              profileImageUrl: event.payload.roomInfo?.streamerInfo?.profileImageUrl || event.payload.profileImageUrl || prev.profileImageUrl
            }));
          }
          break;
      }
    });

    // Request current state from any active stream windows
    streamBroadcastService.requestState();

    return () => {
      unsubscribe();
    };
  }, []);

  // Open stream connector window
  const openStreamConnector = () => {
    // If the window is already open, just bring it to focus — don't reload it
    if (streamConnectorRef.current && !streamConnectorRef.current.closed) {
      streamConnectorRef.current.focus();
      return;
    }

    const width = 400;
    const height = 600;
    const left = window.screen.width - width - 50;
    const top = 50;

    streamConnectorRef.current = window.open(
      '/stream-connector',
      'beemi-stream-connector',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  // Auto-switch layout based on window aspect ratio (only if not manually changed)
  useEffect(() => {
    // Don't auto-switch while loading or during initial setup
    if (isLoadingGame || !game) return;
    
    const handleResize = () => {
      // Skip if in game-only mode or user manually selected layout
      if (layoutMode === 'game-only' || manualLayoutChange) return;
      
      const aspectRatio = window.innerWidth / window.innerHeight;
      
      if (aspectRatio > 1 && layoutMode !== 'landscape') {
        // Wide window - switch to landscape
        setLayoutMode('landscape');
      } else if (aspectRatio <= 1 && layoutMode !== 'portrait') {
        // Tall window - switch to portrait
        setLayoutMode('portrait');
      }
    };
    
    // Only check on resize events, not immediately on mount
    // This prevents interfering with the initial portrait setup for popups
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [layoutMode, manualLayoutChange, isLoadingGame, game]);

  // Handle manual layout selection from dropdown
  const handleLayoutChange = (newLayout: LayoutMode) => {
    // Track layout change in Amplitude
    if (game) {
      trackGameAction('layout_changed', game.id.toString(), {
        game_name: game.name,
        from_layout: layoutMode,
        to_layout: newLayout
      });
    }
    
    setLayoutMode(newLayout);
    setManualLayoutChange(true);
    setShowLayoutMenu(false);
    
    // Resize window to appropriate dimensions if in popup
    if (isPopup && window.resizeTo) {
      const screenWidth = window.screen.availWidth;
      const screenHeight = window.screen.availHeight;
      const safeWidth = screenWidth * 0.8;
      const safeHeight = screenHeight * 0.9;
      
      if (newLayout === 'portrait') {
        // Portrait: Use 90% of screen height, compute width from aspect
        // Game takes 60% of height, game aspect is 500:600 (5:6)
        // If height = H, game height = 0.6 * H, scale = (0.6 * H) / 600
        // Scaled game width = 500 * scale = 500 * 0.6 * H / 600 = 0.5 * H
        // Window width = scaled game width
        const height = safeHeight;
        const width = height * 0.44; // Derived from game aspect and 60% ratio
        window.resizeTo(Math.round(width), Math.round(height));
      } else if (newLayout === 'landscape') {
        // Landscape: Use 90% of screen width, compute height from aspect
        // Game takes 50% of width, game aspect is 500:600 (5:6)
        // If width = W, game width = 0.5 * W, scale = (0.5 * W) / 500
        // Scaled game height = 600 * scale = 600 * 0.5 * W / 500 = 0.6 * W
        // Window height = scaled game height
        const width = safeWidth;
        const height = width * 0.6; // Derived from game aspect and 50% ratio
        window.resizeTo(Math.round(width), Math.round(height));
      } else {
        // Game-only: 4:4.8 content aspect ratio, account for ~60px browser chrome
        const browserChrome = 60;
        const contentHeight = Math.round(screenHeight * 0.85) - browserChrome;
        const contentWidth = Math.round(contentHeight * (4 / 4.8));
        window.resizeTo(contentWidth, contentHeight + browserChrome);
      }
    }
    
    // Reset manual flag after a delay to allow auto-switching again
    setTimeout(() => {
      setManualLayoutChange(false);
    }, 1000);
  };

  // Calculate scale based on available space in the PARENT container
  useEffect(() => {
    // Don't run if game is still loading (ref won't be available)
    if (isLoadingGame || !game) return;
    
    const calculateScale = () => {
      if (!parentContainerRef.current) return;
      
      // Measure the parent container (total available space)
      const parent = parentContainerRef.current;
      const parentWidth = parent.clientWidth;
      const parentHeight = parent.clientHeight;
      
      // Skip if dimensions aren't ready yet
      if (parentWidth === 0 || parentHeight === 0) return;
      
      // Fixed game dimensions (after 1.25 internal scale)
      const gameWidth = 500;
      const gameHeight = 600;
      
      let scale: number;
      
      if (layoutMode === 'landscape') {
        // In landscape, game is on the right - max 50% of width
        const scaleByHeight = parentHeight / gameHeight;
        const scaleByWidth = (parentWidth * 0.5) / gameWidth; // Max 50% of width for game
        scale = Math.min(scaleByHeight, scaleByWidth);
      } else if (layoutMode === 'portrait') {
        // In portrait, game is at bottom - max 60% of height
        const scaleByWidth = parentWidth / gameWidth;
        const scaleByHeight = (parentHeight * 0.6) / gameHeight; // Max 60% of height for game
        scale = Math.min(scaleByWidth, scaleByHeight);
      } else {
        // Game-only mode - fit to available space
        const scaleByWidth = parentWidth / gameWidth;
        const scaleByHeight = parentHeight / gameHeight;
        scale = Math.min(scaleByWidth, scaleByHeight);
      }
      
      // Only update if scale has actually changed (prevent loops)
      setGameScale(prev => Math.abs(prev - scale) > 0.001 ? scale : prev);
    };
    
    // Wait for next frame to ensure DOM is ready
    let rafId = requestAnimationFrame(() => {
      if (!parentContainerRef.current) return;
      
      const resizeObserver = new ResizeObserver(() => {
        calculateScale();
      });
      
      resizeObserver.observe(parentContainerRef.current);
      
      // Calculate after observer is set up
      calculateScale();
      
      // Additional delayed calculations for edge cases
      const timer1 = setTimeout(calculateScale, 50);
      const timer2 = setTimeout(calculateScale, 200);
      
      // Store cleanup refs
      (window as any).__beemiScaleCleanup = () => {
        resizeObserver.disconnect();
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    });
    
    return () => {
      cancelAnimationFrame(rafId);
      if ((window as any).__beemiScaleCleanup) {
        (window as any).__beemiScaleCleanup();
        delete (window as any).__beemiScaleCleanup;
      }
    };
  }, [layoutMode, isLoadingGame, game]);
  
  // Loading state
  
  // In preview mode, show an error if psu is missing
  if (previewMode && !previewServerUrl) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center px-6">
          <h1 className="text-xl font-bold text-white mb-3">No preview available</h1>
          <p className="text-gray-400 text-sm">Open this page from the Studio editor using the Preview button.</p>
        </div>
      </div>
    );
  }

  // Gate on game being present — acts as null guard for TypeScript below (skipped in preview mode)
  if (!previewMode && (gameError || !game)) {
    // Still fetching — show the loading overlay instead of the error page
    if (isLoadingGame || (!gameError && !game)) {
      return (
        <div className="fixed inset-0 bg-gray-950 flex flex-col items-center justify-center gap-5 z-[9999]">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-14 h-14 text-white opacity-90">
            <line x1="6" y1="11" x2="10" y2="11" />
            <line x1="8" y1="9" x2="8" y2="13" />
            <line x1="15" y1="12" x2="15.01" y2="12" strokeWidth="2.5" />
            <line x1="18" y1="10" x2="18.01" y2="10" strokeWidth="2.5" />
            <path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" />
          </svg>
          <div className="flex flex-col items-center gap-3">
            <p className="text-white text-sm font-medium tracking-wide">Loading</p>
            <div className="flex gap-1.5">
              {[0, 1, 2].map(i => (
                <span key={i} className="w-1.5 h-1.5 rounded-full bg-white" style={{ animation: `beemi-bounce 1s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
          </div>
          <style>{`@keyframes beemi-bounce { 0%,80%,100%{transform:translateY(0);opacity:.3} 40%{transform:translateY(-6px);opacity:1} }`}</style>
        </div>
      );
    }
    // Confirmed error after fetch completed
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-white">{gameError || 'Game not found'}</h1>
          {isPopup ? (
            <button
              onClick={() => window.close()}
              className="btn-primary"
            >
              Close
            </button>
          ) : (
            <button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Go Home
            </button>
          )}
        </div>
      </div>
    );
  }
  
  // Get author display name (null in preview mode)
  const authorName = game?.author?.name || game?.team_name || 'Unknown';

  const renderGameBackground = () => {
    if (previewMode) return null;
    // Use game's cover image or icon as background
    // Clean light gray placeholder with centered Lucide Gamepad2 icon
    const backgroundImage = game?.cover_image_url || game?.icon_url || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23f1f5f9"/%3E%3Cg transform="translate(200,150) scale(4.7) translate(-12,-12)"%3E%3Cline x1="6" y1="11" x2="10" y2="11" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="8" y1="9" x2="8" y2="13" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="15" y1="12" x2="15.01" y2="12" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cline x1="18" y1="10" x2="18.01" y2="10" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3Cpath d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" fill="none" stroke="%2364748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3C/g%3E%3C/svg%3E';
    
    return (
      <>
        {/* Blurred background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            filter: 'blur(40px)',
            transform: 'scale(1.1)' // Slight scale to hide blur edges
          }}
        />
        
        {/* Light overlay for better contrast */}
        <div className="absolute inset-0 bg-black bg-opacity-10" />
      </>
    );
  };

  const renderGameIframe = () => {
    let gameUrl: string;

    if (previewMode) {
      // Preview mode — use the preview server URL passed via ?psu= param
      gameUrl = previewServerUrl;
      if (!gameUrl.includes('?')) {
        gameUrl += '?webplay=true';
      } else if (!gameUrl.includes('webplay=true')) {
        gameUrl += '&webplay=true';
      }
      if (authService.isAuthenticated()) {
        const tokens = authService.getStoredTokens();
        if (tokens?.accessToken) {
          gameUrl += `&token=${tokens.accessToken}`;
        }
      }
    } else {
      // Normal mode — use game_url from API response
      gameUrl = game?.game_url || `/games/${game?.id}/`;
      console.log('🎮 [PlayPage] Original game URL:', gameUrl);

      const isFullUrl = gameUrl.startsWith('http://') || gameUrl.startsWith('https://');

      if (!isFullUrl) {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
        gameUrl = `${apiBaseUrl}${gameUrl}`;
        console.log('🎮 [PlayPage] Converted to absolute URL:', gameUrl);
      }

      if (!gameUrl.includes('?')) {
        gameUrl += '?webplay=true';
      } else if (!gameUrl.includes('webplay=true')) {
        gameUrl += '&webplay=true';
      }

      if (isFullUrl && authService.isAuthenticated()) {
        const tokens = authService.getStoredTokens();
        if (tokens?.accessToken) {
          gameUrl += `&token=${tokens.accessToken}`;
          console.log('🎮 [PlayPage] Added auth token');
        }
      }

      console.log('🎮 [PlayPage] FINAL game URL:', gameUrl);
    }
    
    // Track iframe load start time
    if (iframeLoadStartTime.current === 0) {
      iframeLoadStartTime.current = Date.now();
    }
    
    return (
      <div className="relative z-10 overflow-hidden" style={{
        width: '400px',
        height: '480px',
        transform: 'scale(1.25)',
        transformOrigin: 'center center'
      }}>
        <iframe
          ref={gameIframeRef}
          src={gameUrl}
          className="border-0"
          title={previewMode ? 'Studio Preview' : (game?.name || 'Game')}
          sandbox="allow-scripts allow-same-origin allow-forms"
          allow="autoplay; fullscreen"
          onLoad={handleIframeLoad}
          style={{
            width: '400px',
            height: '480px'
          }}
        />
        {/* SDK Status Indicator (debug) */}
        {isAttached && (
          <div className="absolute top-2 right-2 z-20">
            <div 
              className={`w-2 h-2 rounded-full ${isSDKReady ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`}
              title={isSDKReady ? `SDK Ready (v${sdkVersion})` : 'Waiting for SDK...'}
            />
          </div>
        )}
      </div>
    );
  };

  const renderLayout = () => {
    switch (layoutMode) {
      case 'game-only':
        return (
          <div 
            ref={parentContainerRef}
            className="flex-1 flex items-center justify-center relative" 
            style={{ overflow: 'hidden' }}
          >
            {renderGameBackground()}
            <div style={{
              width: `${500 * gameScale}px`,
              height: `${600 * gameScale}px`,
              overflow: 'hidden',
              position: 'relative',
              zIndex: 10
            }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '500px',
                height: '600px',
                transform: `translate(-50%, -50%) scale(${gameScale})`,
                transformOrigin: 'center center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {renderGameIframe()}
              </div>
            </div>
          </div>
        );
      
      case 'portrait':
        return (
          <div ref={parentContainerRef} className="flex-1 flex flex-col overflow-hidden">
            {/* Top Section - Camera Preview (takes remaining space) */}
            <div className="flex-1 relative bg-gray-900 overflow-hidden" style={{ minHeight: 0 }}>
              <CameraPreview />
            </div>

            {/* Bottom Section - Game WebView (sized to scaled game) */}
            <div 
              className="relative flex items-center justify-center" 
              style={{ 
                height: `${600 * gameScale}px`,
                flexShrink: 0,
                overflow: 'hidden'
              }}
            >
              {renderGameBackground()}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '500px',
                height: '600px',
                transform: `translate(-50%, -50%) scale(${gameScale})`,
                transformOrigin: 'center center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}>
                {renderGameIframe()}
              </div>
            </div>
          </div>
        );
      
      case 'landscape':
        return (
          <div ref={parentContainerRef} className="flex-1 flex flex-row overflow-hidden">
            {/* Left Section - Camera Preview (takes remaining space) */}
            <div className="flex-1 relative bg-gray-900 overflow-hidden" style={{ minWidth: 0 }}>
              <CameraPreview />
            </div>

            {/* Right Section - Game WebView (sized to scaled game) */}
            <div 
              className="relative flex items-center justify-center" 
              style={{ 
                width: `${500 * gameScale}px`,
                flexShrink: 0,
                overflow: 'hidden'
              }}
            >
              {renderGameBackground()}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '500px',
                height: '600px',
                transform: `translate(-50%, -50%) scale(${gameScale})`,
                transformOrigin: 'center center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}>
                {renderGameIframe()}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Platform colors and icons
  const platformStyles: Record<StreamingPlatform, { color: string; icon: string }> = {
    tiktok: { color: '#FF0050', icon: '🎬' },
    youtube: { color: '#FF0000', icon: '📺' },
    twitch: { color: '#9146FF', icon: '🎮' }
  };

  return (
    <div className="h-screen bg-dark-bg flex flex-col overflow-hidden">
      {/* Full-screen loading overlay — fades out once iframe fires onLoad */}
      {!isOverlayGone && (
        <div
          className="fixed inset-0 bg-gray-950 flex flex-col items-center justify-center gap-5 z-[9999] pointer-events-none"
          style={{ opacity: isOverlayFading ? 0 : 1, transition: 'opacity 0.6s ease' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-14 h-14 text-white opacity-90">
            <line x1="6" y1="11" x2="10" y2="11" />
            <line x1="8" y1="9" x2="8" y2="13" />
            <line x1="15" y1="12" x2="15.01" y2="12" strokeWidth="2.5" />
            <line x1="18" y1="10" x2="18.01" y2="10" strokeWidth="2.5" />
            <path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z" />
          </svg>
          <div className="flex flex-col items-center gap-3">
            <p className="text-white text-sm font-medium tracking-wide">Loading</p>
            <div className="flex gap-1.5">
              {[0, 1, 2].map(i => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-white"
                  style={{ animation: `beemi-bounce 1s ease-in-out ${i * 0.2}s infinite` }}
                />
              ))}
            </div>
          </div>
          <style>{`
            @keyframes beemi-bounce {
              0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
              40% { transform: translateY(-6px); opacity: 1; }
            }
          `}</style>
        </div>
      )}
      {/* Compact Header */}
      <header className="bg-dark-surface border-b border-dark-border flex-shrink-0 z-50 shadow-lg">
        <div className="px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={() => {
                if (gameIframeRef.current) {
                  setIsIframeReady(false);
                  setIsOverlayFading(false);
                  setIsOverlayGone(false);
                  gameIframeRef.current.src = gameIframeRef.current.src;
                }
              }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors flex-shrink-0"
              title="Restart game"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            {previewMode ? (
              <span className="flex items-center gap-1.5 -my-2 self-stretch px-3 bg-white/[0.06] text-yellow-400 text-sm font-bold tracking-widest uppercase flex-shrink-0">
                <AlertTriangle className="w-3.5 h-3.5" />
                Preview Mode
              </span>
            ) : (
              <>
                <h1 className="text-base font-bold text-white truncate">{game?.name}</h1>
                {game?.play_instructions && (
                  <button
                    onClick={() => setShowInstructions(true)}
                    className="p-1 rounded-full text-gray-400 hover:text-white transition-colors flex-shrink-0"
                    title="How to play"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                )}
                <span className="text-xs text-gray-400 hidden sm:inline flex-shrink-0">by {game?.author?.is_team ? '🏢' : '@'}{authorName}</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Layout Selector */}
            <div className="relative">
            <button
              onClick={() => setShowLayoutMenu(!showLayoutMenu)}
              className="flex items-center gap-1 p-1.5 rounded-lg hover:bg-gray-700 transition-colors text-gray-300 hover:text-white flex-shrink-0"
              title="Change layout"
            >
              <Layout className="w-4 h-4" />
              <ChevronDown className="w-3 h-3" />
            </button>

            {/* Layout Menu Dropdown */}
            {showLayoutMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowLayoutMenu(false)}
                />
                <div className="fixed bg-dark-surface rounded-lg shadow-lg border border-dark-border overflow-hidden z-20 w-56" style={{ top: 44, right: 8 }}>
                  <div className="px-3 py-2 border-b border-dark-border">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Layout</span>
                  </div>
                  <div className="p-2 space-y-1">
                    <button
                      onClick={() => handleLayoutChange('game-only')}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        layoutMode === 'game-only'
                          ? 'bg-primary-500 text-white'
                          : 'hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <Square className="w-4 h-4 flex-shrink-0" />
                      <span>Original</span>
                    </button>
                    <button
                      onClick={() => handleLayoutChange('portrait')}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        layoutMode === 'portrait'
                          ? 'bg-primary-500 text-white'
                          : 'hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <Rows className="w-4 h-4 flex-shrink-0" />
                      <span>Portrait with Camera</span>
                    </button>
                    <button
                      onClick={() => handleLayoutChange('landscape')}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        layoutMode === 'landscape'
                          ? 'bg-primary-500 text-white'
                          : 'hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <Columns className="w-4 h-4 flex-shrink-0" />
                      <span>Landscape with Camera</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

            {/* Stream Connection Badge / Button */}
            {streamInfo.isConnected && streamInfo.platform ? (
              <button
                onClick={openStreamConnector}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shadow-lg hover:shadow-xl hover:scale-105 bg-green-500/20 text-green-400"
                title="Open stream connector"
              >
                <span className="w-2 h-2 rounded-full animate-pulse bg-green-400" />
                <span className="font-bold tracking-wide">LIVE</span>
                <span className="hidden sm:inline text-white/70">@{streamInfo.username}</span>
                {streamInfo.viewerCount > 0 && (
                  <span className="text-gray-400">• {streamInfo.viewerCount}</span>
                )}
              </button>
            ) : (
              <button
                onClick={openStreamConnector}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gray-700 hover:bg-gray-600 transition-all shadow-lg hover:shadow-xl hover:scale-105"
                title="Connect to stream"
              >
                <Radio className="w-3.5 h-3.5" />
                <span className="hidden min-[280px]:inline">Connect Stream</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Dynamic Layout */}
      {renderLayout()}

      {/* Streaming Limits Overlay - Shows remaining time for free tier users (not shown in preview mode) */}
      {!previewMode && !streamingLimits.isUnlimited && streamInfo.isConnected && streamInfo.platform && (
        <StreamingLimitsOverlay
          remainingTime={streamingLimits.remainingTime}
          isVisible={true}
          onUpgradePress={() => setShowUpgradeModal(true)}
        />
      )}

      {/* How to Play Modal */}
      {showInstructions && game && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={() => setShowInstructions(false)}
        >
          <div
            className="bg-dark-surface border border-dark-border rounded-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border">
              <div className="flex items-center gap-3">
                {game.icon_url && (
                  <img src={game.icon_url} alt={game.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                )}
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">How to Play</p>
                  <h2 className="text-sm font-bold text-white leading-tight">{game.name}</h2>
                </div>
              </div>
              <button
                onClick={() => setShowInstructions(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Instructions */}
            <div className="px-5 py-4 max-h-72 overflow-y-auto">
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">{game.play_instructions}</p>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal - Simple version for now (not shown in preview mode) */}
      {!previewMode && showUpgradeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100]" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-dark-surface border border-dark-border rounded-xl p-8 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-4">Upgrade to Premium</h2>
            <p className="text-gray-300 mb-6">
              Get unlimited streaming time and access to all premium features with Beemi Premium.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  // TODO: Navigate to subscription/upgrade page when implemented
                  console.log('Navigate to upgrade page');
                }}
                className="flex-1 py-3 px-6 rounded-lg font-semibold text-white"
                style={{ background: LINE_GRADIENT }}
              >
                Learn More
              </button>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-6 py-3 rounded-lg font-semibold text-gray-300 hover:bg-dark-surface-secondary transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

